import {
  deselect, deselectEditor, findNodePath, PlateRenderElementProps,
} from '@udecode/plate-core';
import { DreamValue, CollectionElement as CollectionElementType } from '@typedream/core';
import {
  CollectionFilter, VISITOR_FILTER_OPTION_ALL, VISITOR_FILTER_STYLE_OPTIONS, CollectionSearch,
} from '@typedream/components';
import { useFocused, useReadOnly } from 'slate-react';
import type { Path } from 'slate';
import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import classNames from 'classnames';
import { ChevronDown } from '@styled-icons/bootstrap';
import { isEqual } from 'lodash';
import { Info } from '@styled-icons/feather';
import { Warning } from '@styled-icons/material';
import {
  COLLECTION_FILTER_CURRENT_ITEM_PREFIX, ICollectionPageData, ISpaceRoute,
} from '@typedream/data';
import Link from 'next/link';
import { isBlockEmpty } from '../../../../editorTools/queries';
import { DreamEditor } from '../../../../types';
import { getTPlugin, useTPlateEditorRef } from '../../../../plate';
import {
  CollectionPluginOptions, ICollectionCursorState,
  ICollectionDataItemMetadataKeys, ICollectionEditor,
} from '../../types';
import { ELEMENT_COLLECTION } from '../../../../pluginKeys';
import { setBlockProperty } from '../../../../editorTools/transforms';
import { DEFAULT_MAX_WIDTH, MOBILE_CUTOFF } from '../../../../constants';
import { getWidthStyle } from '../../../../utils/getWidthStyle';
import Tooltip from '../../../../components/Tooltip';

const Container = styled.div`
  width: 100%;
  box-sizing: border-box;
  min-height: 100px;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  user-select: none !important;
  background-color: var(--background);
  color: var(--accent);
  border-radius: 10px;
  cursor: pointer;
`;

const SelectTemplate = styled.div` 
  margin: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  border-radius: 10px;
  background-color: var(--background-button);
  border: 1px solid var(--border-modal);
  box-shadow: var(--shadow-modal);
  transition: all 0.1s;
  &:hover {
    border: 1px solid var(--accent);
  }
`;

const SpanPaywallTrigger = styled.span`
  height: 20px;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #5E5DEF;
  &:hover {
    text-decoration-line: underline;
    cursor: pointer;
  }
`;
const DivPaywallWarning = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #555555;
`;

const DivPaywallContainer = styled.div`
  display: flex;
  padding: 14px 18px;
  height: 48px;
  left: calc(50% - 523px/2);
  top: 0px;

  background: #FFFFFF;
  box-shadow: 0px 20px 24px -4px rgba(16, 24, 40, 0.1), 0px 8px 8px -4px rgba(16, 24, 40, 0.04);
  border-radius: 12px;
`;

const ContentContainerColumn = css`
  display: grid;
  grid-template-columns: 20% 80%;
  gap: 16px;
  align-items: flex-start;
`;

const WrapperVisitorFilter = styled.div<{ isLeftColumn: boolean }>`
  userSelect: 'none';
  padding: 0px 30px;
  ${(props) => props.isLeftColumn && ContentContainerColumn}
`;
const PreviewContainer = css`
  position: relative;
  opacity: 0.4;
`;
const DivGridContainer = styled.div<{ isErrorExist?: boolean }>`
  width: 100%;
  display: grid;
  grid-gap: 1rem;
  align-content: center;
  justify-content: flex-start;
  ${(props) => props.isErrorExist && PreviewContainer}
`;
const DivPreviewContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  ${PreviewContainer}
`;
const spinAround = keyframes`
0% {
    transform: rotate(0deg);
}
100% {
    transform: rotate(359deg);
}`;

const SpanLoader = styled.span`
  border-bottom-color: var(--accent) !important;
  border-left-color: var(--background-highlight) !important;
  word-break: break-word;
  cursor: text;
  animation-name: ${spinAround};
  animation-duration: 500ms;
  animation-iteration-count: infinite;
  border: 2px solid hsl(0deg, 0%, 86%);
  border-radius: 9999px;
  content: "";
  display: block;
  height: 1em;
  position: relative;
  width: 1em;
  margin-right: 0.25rem !important;
`;

const CursorDiv = styled.div`
  margin-top: 1.5rem !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-direction: row !important;
`;
const PreviewContainerDiv = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;
const PreviewTextDiv = styled.div`
  border-radius: 999px;
  background-color: var(--accent-light);
  border: 1px solid var(--accent);
  box-shadow: var(--shadow-modal);
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 12px;
  color: var(--accent);
  font-size: 14px;
  -webkit-animation-duration: 1s; 
  animation-duration: 1s; 
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both; 
  animation-iteration-count: infinite;
  -webkit-animation-iteration-count: infinite; 
  -webkit-animation-name: bounceInLeft;
  animation-name: bounceInLeft;
  z-index: 1000;
`;

const ContentContainerDiv = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const PreviewImage = styled.img<{ imageDark?: string; image: string; }>`
  content:url(${(props) => props.image});  
  
  [data-theme="dark"] &{
    content:url(${(props) => props.imageDark});
  }
`;

const ContainerDivWarning = styled.div`
  flex-direction: row;
  justify-content: space-between;
  background-color: var(--background-button);
  box-shadow: var(--shadow-modal);
  border-radius: 4px;
  cursor: pointer;

  border: 1px solid var(--border-modal);

  color: #fa9e34!important;

  margin-bottom: 0.75rem!important;

  padding: 10px;
  font-size: 12px;
  line-height: 12px;
  font-weight: 500;

  display: flex!important;

  align-items: center!important;
`;

const ContainerDivDanger = styled.div`
  color: var(--danger)!important;
  margin-bottom: 0.75rem!important;

  padding: 10px;
  font-size: 12px;
  line-height: 12px;
  font-weight: 500;
  border: 1px solid var(--border-modal);
  justify-content: space-between;
  background-color: var(--background-button);
  box-shadow: var(--shadow-modal);
  border-radius: 4px;
  cursor: pointer;

  display: flex!important;

  align-items: center!important;
`;

const ContainerDivLight = styled.div`
  color: var(--text) !important;
  margin-bottom: 0.75rem!important;

  padding: 10px;
  font-size: 12px;
  line-height: 12px;
  font-weight: 500;
  border: 1px solid var(--border-modal);
  justify-content: space-between;
  background-color: var(--background-button);
  box-shadow: var(--shadow-modal);
  border-radius: 4px;
  cursor: pointer;

  display: flex!important;

  align-items: center!important;
`;

const DivNoResultsText = styled.div`
  font-size: 15px;
  font-weight: 500;

  text-align: left !important;

  width: 100%;
`;

const StyledInfo = styled(Info)`
margin-right: 0.5rem!important;
`;
const StyledWarning = styled(Warning)`
margin-right: 0.5rem!important;
`;

function CollectionElementWrapper(
  props: PlateRenderElementProps<DreamValue, CollectionElementType>,
) {
  const {
    element, attributes, className, children,
  } = props;
  const { width, width_max } = element;
  const readOnly = useReadOnly();
  const editor = useTPlateEditorRef();
  const path = findNodePath(editor, element);
  const [windowWidth, setwindowWidth] = React.useState<number | null>(null);
  const focused = useFocused();
  const numCol = element.collection_template?.settings?.numCol;
  const elementID = element.id;

  function resizeWindowWidth() {
    if (window && window.innerWidth) {
      setwindowWidth(window.innerWidth);
    }
  }
  const { options } = getTPlugin<CollectionPluginOptions>(editor, ELEMENT_COLLECTION);
  const {
    templatePath, settingsOpen, settemplatePath, setsidebarTemplateOpen, collectionData,
    getVisitorFilterList, visitorFilterList, getIsTableConnected, sidebarTemplateOpen,
    showSchema, setcollectionSettingsPath, setsettingsOpen, getNextData, nextDataLoading,
    selectedTemplate, selectedCategory, hovered, space, loadingPage, getItemPageDataID,
    initialValueEmpty, collectionSynced, setcollectionSynced, isContentEqualNoID,
    limit, unlimited,
  } = options as CollectionPluginOptions;
  const collDataItem = collectionData?.[element.collection_data_id];
  const elementTemplate = element.collection_template;
  const showSearch = collDataItem?.metadata?.[
    ICollectionDataItemMetadataKeys.SHOW_SEARCH_BAR];
  const visitorFilterColumn = collDataItem?.metadata?.[
    ICollectionDataItemMetadataKeys.VISITOR_FILTER_COLUMN];
  const visitorFilterStyle = collDataItem?.metadata?.[
    ICollectionDataItemMetadataKeys.VISITOR_FILTER_STYLE];
  const visitorFilterColor = collDataItem?.metadata?.[
    ICollectionDataItemMetadataKeys.VISITOR_FILTER_COLOR];

  const colErrToString = (errMsg: string) => {
    if (errMsg?.includes('finding resource')) {
      return 'Error finding resource. Your integration might have been removed. Click "Edit Collection" to reconnect.';
    }
    if (errMsg?.includes('401')) {
      return (
        <div>
          Unauthorized request. Your API key might not be valid. Try reconnecting the integration in
          {' '}
          <Link href={`/settings/${space?.slug}/${space?.id}/integrations`}>Settings / Integrations.</Link>
        </div>
      );
    }
    return errMsg;
  };

  const filterCurrentItem = collDataItem?.metadata?.filter_value
    && typeof (collDataItem.metadata.filter_value) === 'string'
    && collDataItem.metadata.filter_value.includes(
      COLLECTION_FILTER_CURRENT_ITEM_PREFIX,
    ) ? collDataItem.metadata.filter_value.replace(COLLECTION_FILTER_CURRENT_ITEM_PREFIX, '') : undefined;
  const e = editor as ICollectionEditor;
  const cursorStates = e?.getCursorState(elementID);
  const showHovered = React.useMemo(() => {
    const leftMenuBlockPath = templatePath || [];
    if (isEqual(path, leftMenuBlockPath)) {
      return true;
    }
    return false;
  }, [templatePath, path, editor]);

  // Sync collection when page data and initial value are updated
  React.useEffect(() => {
    e.setspaceCollectionData?.(space?.customization?.collection_item_data as ICollectionPageData);
  }, [e, space?.customization?.collection_item_data]);

  React.useEffect(() => {
    e.setspaceRoutes?.(space?.routes as ISpaceRoute);
  }, [e, space?.routes]);

  React.useEffect(() => {
    if (e.children !== initialValueEmpty
      && collectionData
      && !collectionSynced) {
      e.synchronizeCollection?.(
        collectionData,
      );
      setcollectionSynced?.(true);
    }
  }, [e.children, collectionData]);

  React.useEffect(() => {
    if (!focused && isContentEqualNoID?.(e.children, initialValueEmpty as DreamValue)) {
      deselectEditor(e);
    }
  }, [focused]);

  React.useEffect(() => {
    if (!collDataItem || visitorFilterList?.length) return;
    const itemsPerPage = collDataItem
      ?.metadata?.[ICollectionDataItemMetadataKeys.MAX_ITEMS] as number;
    const itemErr = collDataItem?.err;

    if (!cursorStates
      || cursorStates?.hasMore === undefined
      || cursorStates?.err !== itemErr
      || (cursorStates?.hasMore !== collDataItem?.hasMore
        && cursorStates?.itemsPerPage !== itemsPerPage)) {
      const initialState: ICollectionCursorState = {
        hasMore: collDataItem?.hasMore as boolean,
        nextCursor: collDataItem?.nextCursor as string,
        fetchedCursor: collDataItem?.fetchedCursor as string,
        itemsPerPage,
        data: collDataItem?.data as any,
        err: collDataItem?.err,
        searchQuery: undefined,
      };

      e.setCursorState(elementID, initialState);
    }

    if (collDataItem?.metadata?.visitor_filter_column) {
      getVisitorFilterList(collDataItem);
    }
  }, [collDataItem, visitorFilterList]);

  const isTableConnected = getIsTableConnected?.(collDataItem) as boolean;
  const image = elementTemplate?.previewImage?.image || '/images/collectionTemplates/scratch.png';
  const imageDark = elementTemplate?.previewImage?.imageDark || '/images/collectionTemplates/scratch_dark.png';
  const errCollectionCursor = e.collectionCursors?.[element.id]?.err
    && colErrToString(
      e.collectionCursors?.[element?.id]?.err,
    );

  const errNotConnected = !loadingPage
    && !!element
    && !isBlockEmpty(editor, element)
    && !isTableConnected;

  const itemPageDataID = getItemPageDataID(collDataItem);
  const errNoItemPage = !loadingPage && !!element && !isBlockEmpty(editor, element)
    && !space?.customization?.collection_item_data?.[itemPageDataID]?.childrenTemplatePageID;

  React.useEffect(() => {
    if (window && window.innerWidth) {
      resizeWindowWidth();
    }
  }, []);
  React.useEffect(() => {
    if (!settingsOpen || !templatePath) return;
    setBlockProperty(
      editor,
      'collection_template',
      { templateKey: 'scratch' },
      { at: templatePath || path as Path },
    );
    setcollectionSettingsPath?.(templatePath || path as Path);
    settemplatePath?.(null);
  }, [templatePath, settingsOpen]);
  React.useEffect(() => {
    if (!settingsOpen || !selectedTemplate || !selectedCategory) return;
    setBlockProperty(
      editor,
      'collection_template',
      {
        ...selectedTemplate.template,
        templateKey: `${selectedCategory}:${selectedTemplate.key}`,
      },
      { at: templatePath as Path },
    );
    setBlockProperty(
      editor,
      'collection_default_item_page_template_key',
      `${selectedTemplate.defaultItemPageTemplate?.category}:${selectedTemplate.defaultItemPageTemplate?.key}`,
      { at: templatePath as Path },
    );
    setcollectionSettingsPath?.(templatePath || path as Path);
    settemplatePath?.(null);
  }, [selectedTemplate, settingsOpen, selectedCategory]);

  const openSidebar = () => {
    setTimeout(() => {
      deselect(editor);
    }, 500);
    settemplatePath(path as Path);
    setsidebarTemplateOpen(true);
  };

  if (isBlockEmpty(editor as unknown as DreamEditor, element) && (!readOnly
    || showSchema) && !elementTemplate) {
    return (

      <Container
        {...attributes}
        className={classNames(className)}
        contentEditable={false}
      >
        {sidebarTemplateOpen && hovered && showHovered
          ? (
            <PreviewContainerDiv>
              <PreviewImage
                image={hovered.image}
                imageDark={hovered.imageDark}
                style={{ width: '100%', height: '100%', maxWidth: DEFAULT_MAX_WIDTH }}
                alt="Section Template Preview"
              />
              <PreviewTextDiv>
                ← Preview. Click to apply.
              </PreviewTextDiv>
            </PreviewContainerDiv>
          )
          : (
            <SelectTemplate
              data-testid="CollectionElement_select_button"
              role="none"
              onClick={openSidebar}
            >
              Select Collection Template
            </SelectTemplate>
          )}

        <span style={{ display: 'none' }}>
          {children}
        </span>
      </Container>
    );
  }

  if (isBlockEmpty(editor as unknown as DreamEditor, element) && (!readOnly
    || showSchema) && elementTemplate && !isTableConnected) {
    return (
      <Container
        {...attributes}
        className={classNames(className)}
        contentEditable={false}
      >
        <SelectTemplate
          role="none"
          onClick={() => {
            setTimeout(() => {
              deselect(editor);
            }, 500);
            setcollectionSettingsPath?.(templatePath as Path || path);
            setsettingsOpen(true);
          }}
        >
          Connect Data
        </SelectTemplate>
        <DivPreviewContainer>
          <PreviewImage
            image={image}
            imageDark={imageDark}
            style={{ width: '100%', height: '100%', maxWidth: DEFAULT_MAX_WIDTH }}
            alt="Collection Template Preview"
          />
        </DivPreviewContainer>
        <span style={{ display: 'none' }}>
          {children}
        </span>
      </Container>
    );
  }

  return (
    <WrapperVisitorFilter
      isLeftColumn={visitorFilterStyle === VISITOR_FILTER_STYLE_OPTIONS.LEFT_COLUMN}
      {...attributes}
      style={getWidthStyle(width, width_max)}

    >
      {!unlimited && (
      <div style={{
        width: '100%',
        height: 48,
        marginBottom: 30,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      >
        <DivPaywallContainer>
          <DivPaywallWarning className="with-text-color">
            <Info size={18} style={{ marginTop: -3, marginRight: 8, letterSpacing: '-0.01em' }} color="var(--accent)" className="mr-2" />
            Only
            {' '}
            {limit}
            {' '}
            item will be shown in published version
            <Link href={`/settings/${space?.slug}/${space?.id}/billing`}>
              <SpanPaywallTrigger className="with-accent-text-color">
                {' '}
                Upgrade to show all -
                {'>'}
              </SpanPaywallTrigger>
            </Link>
          </DivPaywallWarning>
        </DivPaywallContainer>

      </div>
      )}
      {visitorFilterColumn && visitorFilterList.length > 0
        && (
          <Tooltip center="You can try out this filter on Preview mode or Published site">
            <div style={{ width: '100%', marginBottom: 16 }}>
              <CollectionFilter
                filters={visitorFilterList}
                filterColor={visitorFilterColor}
                filterStyle={visitorFilterStyle as VISITOR_FILTER_STYLE_OPTIONS}
                selectedFilter={VISITOR_FILTER_OPTION_ALL}
                setSelectedFilter={() => { /** do nothing */ }}
              />
            </div>
          </Tooltip>
        )}
      <ContentContainerDiv>
        {showSearch && <CollectionSearch search={() => { }} searchQuery="" setSearchQuery={() => { }} />}
        {(errCollectionCursor || errNotConnected) && !readOnly && (
          <ContainerDivDanger className="with-danger-text-color mb-3 box-border is-flex is-align-items-center">
            <StyledInfo size={15} className="mr-2" />
            <span>
              {
                errCollectionCursor
              }
              {
                errNotConnected ? 'This collection isn\'t connected properly to a table. Click "Connect Data" to set up' : null
              }
            </span>
          </ContainerDivDanger>
        )}

        {(errNoItemPage) && !readOnly && (
          <ContainerDivWarning className="has-text-warning mb-3 box-border is-flex is-align-items-center">
            <StyledWarning size={15} className="mr-2" />
            <span>
              {
                errNoItemPage ? "This collection doesn't have an Item Page View on Typedream." : null
              }
            </span>
          </ContainerDivWarning>
        )}
        {filterCurrentItem
          && (
            <ContainerDivLight className="with-light-text-color mb-3 box-border is-flex is-align-items-center">
              <StyledInfo size={15} className="mr-2" />
              <span>
                This collection will be filtered by the current item&apos;s
                {' '}
                {filterCurrentItem}
              </span>
            </ContainerDivLight>
          )}

        {
          isBlockEmpty(editor, element)
          && isTableConnected
          && (
            <DivNoResultsText className="is-subtitle has-text-left has-fullwidth">
              No results found
              {showSearch && ', try adjusting your search keyword'}
              .
            </DivNoResultsText>
          )
        }
        <DivGridContainer
          isErrorExist={errNoItemPage || !!errCollectionCursor || errNotConnected}
          contentEditable={false}
          style={{
            userSelect: 'none',
            gridTemplateColumns: windowWidth && windowWidth <= MOBILE_CUTOFF ? undefined : `repeat(${numCol || 'auto-fit'}, minmax(200px, 1fr))`,
          }}
          data-testid="CollectionElement_gridContainer"
        >
          {children}
        </DivGridContainer>
        {e.collectionCursors?.[element?.id]?.hasMore && (
          <CursorDiv
            id={`button-div-${element.id}`}
            className={classNames('mt-5 is-flex is-align-items-center is-justify-content-center is-flex-direction-row')}
            contentEditable={false}
            style={{ userSelect: 'none', color: 'var(--accent)' }}
            role="none"
            onClick={() => getNextData?.(
              true,
              collDataItem,
              cursorStates,
              e.setCursorState,
              editor,
              element,
              e.synchronizeCollection,
              isTableConnected,
            )}
          >
            {nextDataLoading && (
              <SpanLoader />
            )}
            Show more
            <ChevronDown size={18} className="ml-2" />
          </CursorDiv>
        )}

      </ContentContainerDiv>
    </WrapperVisitorFilter>
  );
}

export default CollectionElementWrapper;
